import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApplicationRef, Injectable, Injector, NgZone, runInInjectionContext, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Messaging, getToken, onMessage, getMessaging as _getMessaging } from '@angular/fire/messaging';
import { environment } from '../../../environments/environment';
import { Platform } from '@angular/cdk/platform';
import { Subject, BehaviorSubject, Observable, timer, EMPTY } from 'rxjs';
import { initializeApp } from '@angular/fire/app';
import { switchMap, retry, share, finalize } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { PushNotification } from '@capacitor/push-notifications';
import { PushService } from '../push/push.service';
import { WebSocketService } from '../websocket/websocket.service';
import { DeepLinkService, PENDING_DEEP_LINK_PATH_KEY } from '../deep-link.service';
import {
  PushNavigationService,
  PushNotificationData,
} from '../push/push-navigation.service';


/**
 * PWA tray icon path (shown by firebase-messaging-sw.js only).
 * Backend FCM shape: { notification: { title, body }, data: { route, type, id, ... } }.
 * Web: one OS notification (SW + icon). In-app bell: WebSocket + FCM onMessage/postMessage.
 * Native: Capacitor PushNotifications (system tray); this service syncs the bell only.
 */
export const PUSH_NOTIFICATION_ICON = '/assets/icons/icon-192.webp';
/** Set by SW / click handler when Angular is not ready yet; consumed on app init. */
export const PENDING_PUSH_ROUTE_KEY = 'pendingPushRoute';

/** Device descriptor sent with auth/register/social_login payloads. */
export interface DeviceInfo {
  /** Firebase Cloud Messaging token; empty string when unsupported / permission denied. */
  device_token: string;
  /** Platform tag the backend stores against the user session. */
  device_type: 'web' | 'mobile';
}

export interface NotificationResponse {
  status: boolean;
  code: number;
  result: Notification[];
  lastPage: number;
  loadmore: string;
  err: any;
}

export interface Notification {
  id: number;
  user_id: number;
  type: string;
  action_id: number;
  action_by_user_id: number;
  is_notified: string;
  is_read: string;
  created_at: string;
  updated_at: string;
  profile_img: string;
  from_username: string;
  to_username: string;
  event_name: string;
  event_slug: string;
  message: string;
  title?: string;
  /** Optional slug for redirect (e.g. "training/bookings" for training notifications). */
  slug?: string;
  store_url?: string;
  version?: string;
  update_type?: string;
  route?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private deviceToken: string | null = null;
  private isSupported: boolean = false;
  private messagingInstance: Messaging | null = null;
  private readonly POLLING_INTERVAL_MS = 30000;

  private readonly _notificationReceived$ = new Subject<any>();
  private readonly _isInitialized$ = new BehaviorSubject<boolean>(false);
  private readonly _notifications$ = new BehaviorSubject<Notification[]>([]);
  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  private readonly _lastPage$ = new BehaviorSubject<number>(1);
  private readonly _currentPage$ = new BehaviorSubject<number>(1);
  private readonly _loadingMore$ = new BehaviorSubject<boolean>(false);
  private readonly _isRefreshing$ = new BehaviorSubject<boolean>(false);

  /** Public observables – consumers subscribe only; state is updated internally */
  readonly notificationReceived$ = this._notificationReceived$.asObservable();
  readonly isInitialized$ = this._isInitialized$.asObservable();
  readonly notifications$ = this._notifications$.asObservable();
  readonly unreadCount$ = this._unreadCount$.asObservable();
  readonly lastPage$ = this._lastPage$.asObservable();
  readonly currentPage$ = this._currentPage$.asObservable();
  readonly isLoadingMore$ = this._loadingMore$.asObservable();
  readonly isRefreshing$ = this._isRefreshing$.asObservable();

  private pollingSubscription: any;
  private notificationSubscription: any = null;
  private notificationUpdatedSubscription: any = null;
  private isWebSocketSubscribed = false;
  private readonly pushService = inject(PushService);
  private readonly pushNavigation = inject(PushNavigationService);

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private injector: Injector,
    private platform: Platform,
    private websocketService: WebSocketService,
    private router: Router,
    private deepLinkService: DeepLinkService,
    private appRef: ApplicationRef
  ) {
    this.checkSupport();
    this.setupServiceWorkerMessageListener();
    this.setupNativePushNotificationListener();
    if (this.isBrowser() && this.isAuthenticated()) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
  }

  /** Safe for SSR: avoid localStorage/window when not in browser */
  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }

  private logError(context: string, err: unknown): void {
    if (!environment.production) {
      console.warn(`[NotificationService] ${context}`, err);
    }
  }

  private checkSupport(): void {
    this.isSupported =
      this.isBrowser() &&
      !Capacitor.isNativePlatform() &&
      !this.platform.SAFARI &&
      typeof (window as any).Notification !== 'undefined';
  }

  // Helper to get Messaging only if supported and provided
  private getMessaging(): Messaging | null {
    if (!this.isSupported) {
      return null;
    }

    if (this.messagingInstance) {
      return this.messagingInstance;
    }

    try {
      return runInInjectionContext(this.injector, () => {
        if (!this.messagingInstance) {
          const app = initializeApp(environment.firebaseConfig);
          this.messagingInstance = _getMessaging(app);
        }
        return this.messagingInstance;
      });
    } catch (error) {
      console.error('Error getting messaging instance:', error);
      return null;
    }
  }

  private isAuthenticated(): boolean {
    if (!this.isBrowser()) return false;
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    return !!(token && userId);
  }

  private startPollingIfAuthenticated() {
    // This method is kept for backward compatibility but polling is replaced with WebSocket
    if (this.isAuthenticated()) {
      // Polling disabled - using WebSocket instead
    }
  }

  /**
   * Public method to start WebSocket subscription after authentication
   * Should be called after successful login
   */
  startPollingAfterAuth() {
    if (!this.isWebSocketSubscribed) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
    if (this.pushService.isNativePushAvailable()) {
      void this.pushService.syncDeviceTokenIfAuthenticated();
    }
  }

  private startPolling(): void {
    this.pollingSubscription = timer(0, this.POLLING_INTERVAL_MS).pipe(
      switchMap(() => {
        // Check authentication before each API call
        if (!this.isAuthenticated()) {
          // Stop polling if user is not authenticated
          this.stopPolling();
          return EMPTY;
        }
        return this.getNotifications();
      }),
      retry({
        delay: 5000 // Retry after 5 seconds if there's an error
      }),
      share()
    ).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          const list = this.normalizeNotificationList(response.result);
          this._notifications$.next(list);
          const unreadCount = list.filter((n: Notification) => n.is_read === 'n').length;
          this._unreadCount$.next(unreadCount);
        }
      },
      error: (err) => this.logError('polling', err)
    });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  private subscribeToNotifications(): void {
    if (!this.isAuthenticated() || this.isWebSocketSubscribed) {
      return;
    }

    const userId = localStorage.getItem('user_id');
    if (!userId) return;

    try {
      this.websocketService.subscribeToNotifications(userId);
      this.isWebSocketSubscribed = true;

      this.notificationSubscription = this.websocketService.onNotification().subscribe({
        next: (notification: any) => {
          this.zone.run(() => this.handleNewNotification(notification));
        },
        error: (err) => this.logError('WebSocket notification stream', err)
      });

      this.notificationUpdatedSubscription = this.websocketService.onNotificationUpdated().subscribe({
        next: (notification: any) => {
          this.zone.run(() => this.handleUpdatedNotification(notification));
        },
        error: (err) => this.logError('WebSocket notification.updated stream', err)
      });
    } catch (err) {
      this.logError('subscribeToNotifications', err);
    }
  }

  private normalizeNotificationList(items: Notification[]): Notification[] {
    return items.map((n) => this.normalizeNotification(n));
  }

  private normalizeNotification(n: Notification): Notification {
    if (!PushNavigationService.isAppUpdateType(n.type)) {
      return n;
    }
    const storeUrl = PushNavigationService.resolveAppUpdateStoreUrl(n);
    return storeUrl ? { ...n, store_url: storeUrl, slug: storeUrl } : n;
  }

  private parseNotificationPayload(notification: any): Notification | null {
    const data = notification?.notification ?? notification;
    if (!data || (data.id == null && data.id !== 0)) {
      return null;
    }

    return {
      id: data.id,
      user_id: data.user_id,
      type: data.type,
      action_id: data.action_id,
      action_by_user_id: data.action_by_user_id,
      is_notified: data.is_notified || 'n',
      is_read: data.is_read || 'n',
      created_at: data.created_at,
      updated_at: data.updated_at,
      profile_img: data.profile_img || '',
      from_username: data.from_username || '',
      to_username: data.to_username || '',
      event_name: data.event_name || '',
      event_slug: data.event_slug || '',
      message: data.message || '',
      title: data.title,
      slug: data.slug,
      store_url: data.store_url,
      version: data.version,
      update_type: data.update_type,
      route: data.route,
    };
  }

  private handleNewNotification(notification: any): void {
    const parsed = this.parseNotificationPayload(notification);
    const newNotification = parsed ? this.normalizeNotification(parsed) : null;
    if (!newNotification) {
      this.logError('handleNewNotification: invalid payload (missing id)', { notification: !!notification });
      return;
    }

    const currentNotifications = this._notifications$.value;
    if (currentNotifications.some(n => n.id === newNotification.id)) return;

    this._notifications$.next([newNotification, ...currentNotifications]);
    if (newNotification.is_read === 'n') {
      this._unreadCount$.next(this._unreadCount$.value + 1);
    }

    // In-app list only — web OS tray alerts come from firebase-messaging-sw.js only.
    this.scheduleUiRefresh();
  }

  private handleUpdatedNotification(notification: any): void {
    const parsed = this.parseNotificationPayload(notification);
    const updatedNotification = parsed ? this.normalizeNotification(parsed) : null;
    if (!updatedNotification) {
      this.logError('handleUpdatedNotification: invalid payload (missing id)', { notification: !!notification });
      return;
    }

    const currentNotifications = this._notifications$.value;
    const index = currentNotifications.findIndex(n => n.id === updatedNotification.id);

    if (index === -1) {
      this._notifications$.next([updatedNotification, ...currentNotifications]);
    } else {
      const next = [...currentNotifications];
      next[index] = { ...next[index], ...updatedNotification };
      this._notifications$.next(next);
    }

    const unreadCount = this._notifications$.value.filter(n => n.is_read === 'n').length;
    this._unreadCount$.next(unreadCount);

    this.scheduleUiRefresh();
  }

  /** Force Angular to repaint after WebSocket-driven state changes (Pusher runs outside NgZone). */
  private scheduleUiRefresh(): void {
    this.zone.run(() => this.appRef.tick());
  }

  private setNotificationsPage(response: NotificationResponse): void {
    const list = this.normalizeNotificationList(response?.result ?? []);
    this.zone.run(() => {
      this._notifications$.next(list);
      this._unreadCount$.next(list.filter((n) => n.is_read === 'n').length);
      if (response?.lastPage != null) {
        this._lastPage$.next(response.lastPage);
      }
      this._currentPage$.next(1);
      this.scheduleUiRefresh();
    });
  }

  private loadInitialNotifications(): void {
    this.getNotifications(1).subscribe({
      next: (response: NotificationResponse) => this.setNotificationsPage(response),
      error: (err) => this.logError('loadInitialNotifications', err)
    });
  }

  /**
   * Load next page and append to notifications. Single source of truth for list + pagination.
   */
  loadMoreNotifications(): void {
    if (this._loadingMore$.value) return;
    const current = this._currentPage$.value;
    const last = this._lastPage$.value;
    if (current >= last) return;

    this._loadingMore$.next(true);
    const nextPage = current + 1;

    this.getNotifications(nextPage).subscribe({
      next: (response: NotificationResponse) => {
        if (response?.status && response?.result?.length) {
          const list = [
            ...this._notifications$.value,
            ...this.normalizeNotificationList(response.result),
          ];
          this._notifications$.next(list);
          this._unreadCount$.next(list.filter((n) => n.is_read === 'n').length);
        }
        if (response?.lastPage != null) {
          this._lastPage$.next(response.lastPage);
        }
        this._currentPage$.next(nextPage);
        this._loadingMore$.next(false);
      },
      error: (err) => {
        this.logError('loadMoreNotifications', err);
        this._loadingMore$.next(false);
      }
    });
  }

  /**
   * Reload page 1 (e.g. bell open or push). Pass showLoading true when opening the dropdown.
   */
  refreshNotifications(showLoading = false): void {
    if (showLoading && this._isRefreshing$.value) {
      return;
    }
    if (showLoading) {
      this._isRefreshing$.next(true);
    }

    this.getNotifications(1)
      .pipe(
        finalize(() => {
          if (showLoading) {
            this._isRefreshing$.next(false);
          }
        })
      )
      .subscribe({
        next: (response: NotificationResponse) => this.setNotificationsPage(response),
        error: (err) => this.logError('refreshNotifications', err),
      });
  }

  private unsubscribeFromNotifications(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.notificationUpdatedSubscription) {
      this.notificationUpdatedSubscription.unsubscribe();
      this.notificationUpdatedSubscription = null;
    }
    this.websocketService.unsubscribeFromNotifications();
    this.isWebSocketSubscribed = false;
  }

  /**
   * Call on logout so the next login can subscribe again and state is clean.
   * Safe to call even when not subscribed.
   */
  teardownForLogout(): void {
    this.unsubscribeFromNotifications();
    this._notifications$.next([]);
    this._unreadCount$.next(0);
    this._lastPage$.next(1);
    this._currentPage$.next(1);
    this._isRefreshing$.next(false);
  }

  getNotifications(page: number = 1): Observable<NotificationResponse> {
    if (!this.isBrowser() || !this.isAuthenticated()) {
      return new Observable<NotificationResponse>(observer => observer.complete());
    }

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      userid: localStorage.getItem('user_id'),
      page: page,
      limit: 8
    };

    return this.http.post<NotificationResponse>(
      `${environment.apiUrl}/getAllNotification`,
      payload,
      { headers }
    );
  }

  async markAllNotificationsAsRead() {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id : localStorage.getItem('user_id')
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadAllNotification`, payload, { headers })
        .toPromise();

      const currentNotifications = this._notifications$.value;
      this._notifications$.next(currentNotifications.map(n => ({ ...n, is_read: 'y' })));
      this._unreadCount$.next(0);
    } catch (error) {
      this.logError('markAllNotificationsAsRead', error);
      throw error;
    }
  }

  async markNotificationAsRead(notificationId: number) {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id: localStorage.getItem('user_id'),
      notification_id: notificationId
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadNotification`, payload, { headers })
        .toPromise();

      const currentNotifications = this._notifications$.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === notificationId ? { ...n, is_read: 'y' } : n
      );
      this._notifications$.next(updatedNotifications);
      this._unreadCount$.next(updatedNotifications.filter(n => n.is_read === 'n').length);
    } catch (error) {
      this.logError('markNotificationAsRead', error);
      throw error;
    }
  }

  notificationSettings(
    chat_notification: string,
    email_notification: string,
    event_notification: string
  ) {
    const payload = {
      user_id: localStorage.getItem('user_id'),
      chat_notification,
      email_notification,
      event_notification
    };

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.http.post(environment.apiUrl + '/updateNotificationSettings', payload, {
      headers
    });
  }

  /** Call from a user interaction (e.g. button click) to request push permission */
  async initializeNotifications(): Promise<void> {
    if (!this.isBrowser()) return;
    if (this.pushService.isNativePushAvailable()) {
      await this.pushService.initPush();
      return;
    }

    if (!this.isSupported) return;

    try {
      const permission = await (window as any).Notification.requestPermission();

      if (permission === 'granted') {
        const token = await this.fetchDeviceToken();
        if (token) {
          await this.setupMessageListener();
        }
      } else {
        console.warn('Notification permission not granted');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    }
  }

  private setupServiceWorkerMessageListener(): void {
    if (!this.isBrowser() || !('serviceWorker' in (window as any).navigator)) return;
    const nav = (window as any).navigator;
    if (!nav.serviceWorker) return;

    const onSwMessage = (event: MessageEvent) => {
      if (!event?.data?.type) return;
      if (event.data.type === 'PUSH_NOTIFICATION') {
        this.zone.run(() => this.handleNotificationPayload(event.data.payload));
      } else if (event.data.type === 'NOTIFICATION_CLICKED') {
        this.zone.run(() => this.handleNotificationClick(event.data.payload));
      }
    };

    nav.serviceWorker.addEventListener('message', onSwMessage);
    nav.serviceWorker.ready.then(() => {
      this.logPush('Service worker ready for push click messages');
    }).catch((err: unknown) => this.logError('serviceWorker.ready', err));
  }

  /** Call once after app bootstrap to handle clicks that arrived before Router was ready. */
  consumePendingPushNavigation(): void {
    if (!this.isBrowser()) return;
    const route =
      sessionStorage.getItem(PENDING_PUSH_ROUTE_KEY) ||
      localStorage.getItem(PENDING_DEEP_LINK_PATH_KEY);
    if (!route || route === '/') return;
    sessionStorage.removeItem(PENDING_PUSH_ROUTE_KEY);
    if (!this.isAuthenticated()) return;
    this.logPush('Consuming pending push route', route);
    this.router.navigateByUrl(route).catch(() => {});
  }

  private logPush(message: string, detail?: unknown): void {
    if (!environment.production) {
      console.log('[Push]', message, detail ?? '');
    }
  }

  private stashPendingPushRoute(route: string): void {
    if (!this.isBrowser() || !route || route === '/') return;
    sessionStorage.setItem(PENDING_PUSH_ROUTE_KEY, route);
    this.deepLinkService.setPendingDeepLinkPath(route);
  }

  /** Android/iOS: native layer dispatches `pushNotificationOpen` with `{ route }`. */
  private setupNativePushNotificationListener(): void {
    if (!this.isBrowser() || !Capacitor.isNativePlatform()) return;
    window.addEventListener('pushNotificationOpen', ((event: Event) => {
      const route = (event as CustomEvent<{ route?: string }>).detail?.route;
      if (!route) return;
      this.zone.run(() => this.handleNotificationClick({ route }));
    }) as EventListener);
  }

  /** Push redirect uses only FCM `data.route` (string). */
  private resolveRouteFromPushPayload(payload: Record<string, unknown> | null | undefined): string | null {
    if (!payload) return null;
    const data = (payload['data'] as Record<string, unknown>) ?? payload;
    const route = data['route'] ?? payload['route'];
    if (route == null || route === '') return null;
    return this.normalizePushRoute(String(route));
  }

  private normalizePushRoute(route: string): string {
    const path = route.trim();
    if (!path) return '';
    return path.startsWith('/') ? path : `/${path}`;
  }

  private navigateToPushRoute(route: string): void {
    if (!route) {
      this.logPush('No route to navigate');
      return;
    }

    this.stashPendingPushRoute(route);

    if (!this.isAuthenticated()) {
      this.logPush('Not authenticated, redirecting to signin', route);
      this.router.navigate(['/signin']).catch(() => {});
      return;
    }

    this.logPush('Navigating to', route);
    this.router.navigateByUrl(route).then((ok) => {
      if (ok) {
        sessionStorage.removeItem(PENDING_PUSH_ROUTE_KEY);
        localStorage.removeItem(PENDING_DEEP_LINK_PATH_KEY);
      }
    }).catch(() => {
      this.router.navigateByUrl('/').catch(() => {});
    });
  }

  private handleNotificationClick(payload: Record<string, unknown>): void {
    const data = (payload['data'] as Record<string, unknown>) ?? payload;
    const pushData = this.toPushData(data);

    if (PushNavigationService.isAppUpdateType(pushData.type)) {
      if (this.pushNavigation.openAppUpdateStore(pushData)) {
        return;
      }
      const route = pushData.route ? this.normalizePushRoute(pushData.route) : '';
      if (route) {
        this.navigateToPushRoute(route);
      }
      return;
    }

    const route = this.resolveRouteFromPushPayload(payload);
    this.logPush('Notification clicked', { payload, route });

    if (!route) {
      this.logPush('Missing data.route — no navigation');
      return;
    }

    this.navigateToPushRoute(route);
  }

  private toPushData(data: Record<string, unknown>): PushNotificationData {
    const pushData: Record<string, string> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        pushData[key] = String(value);
      }
    }
    return pushData;
  }

  /**
   * FCM handler (foreground onMessage, SW postMessage, native foreground).
   * Expects { notification?: { title, body }, data: { route, type, id, ... } }.
   * Does not show web OS tray — firebase-messaging-sw.js handles background tray UI.
   */
  private handleNotificationPayload(payload: any) {
    try {
      const normalized = this.normalizeFcmPayload(payload);
      const fcmData = normalized.data;
      const isAppUpdate = PushNavigationService.isAppUpdateType(fcmData['type']);

      if (isAppUpdate) {
        this.refreshNotifications();
      }

      const route = this.resolveRouteFromPushPayload({ data: fcmData });
      this._notificationReceived$.next({
        title: PushNavigationService.resolvePushTitle(normalized),
        body: PushNavigationService.resolvePushBody(normalized),
        timestamp: new Date(),
        data: fcmData,
        route
      });
    } catch (err) {
      this.logError('handleNotificationPayload', err);
    }
  }

  /** Align with FCM HTTP v1 / SW push body ({ notification, data } or nested message). */
  private normalizeFcmPayload(payload: any): {
    notification?: { title?: string; body?: string };
    data: Record<string, string>;
  } {
    if (!payload || typeof payload !== 'object') {
      return { data: {} };
    }
    const message = payload['message'];
    if (message && typeof message === 'object') {
      const data = (message['data'] as Record<string, string>) || payload.data || {};
      return {
        notification: message['notification'] || payload.notification,
        data
      };
    }
    return {
      notification: payload.notification,
      data: (payload.data as Record<string, string>) || {}
    };
  }

  private async setupMessageListener() {
    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return;
    }

    try {
      runInInjectionContext(this.injector, () => {
        onMessage(messaging, (payload) => {
          this.zone.run(() => {
            this.handleNotificationPayload(payload);
          });
        });
      });

      this._isInitialized$.next(true);
    } catch (err) {
      this.logError('setupMessageListener', err);
      this._isInitialized$.next(false);
    }
  }

   /** Handle native push payload (foreground) from PushService. */
   handleNativePushPayload(notification: PushNotification): void {
    const data = notification.data || {};
    this.handleNotificationPayload({
      notification: {
        title: notification.title || data['title'],
        body: notification.body || data['body'] || data['message'],
      },
      data,
      fcmOptions: {
        link: data['click_action'] || data['link'] || 'https://theselfwish.com',
      },
    });
  }

  
  /**
   * Resolve the current platform tag used by the backend to differentiate sessions.
   * Returns 'mobile' for Capacitor/Ionic native builds (iOS or Android) and 'web'
   * for any browser/PWA build.
   */
  getDeviceType(): DeviceInfo['device_type'] {
    return Capacitor.isNativePlatform() ? 'mobile' : 'web';
  }

  /**
   * Convenience helper that returns the Firebase device token + platform tag in a
   * single shape. Components should spread this into login/register/social payloads
   * instead of hand-rolling fallback strings like `'web_token'`.
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    const token = await this.fetchDeviceToken();
    return {
      device_token: token || '',
      device_type: this.getDeviceType(),
    };
  }

  private async getFirebaseMessagingSwRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isBrowser() || !('serviceWorker' in navigator)) return null;
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const fcmSw = registrations.find((r) =>
        r.active?.scriptURL?.includes('firebase-messaging-sw.js')
      );
      return fcmSw ?? (await navigator.serviceWorker.ready);
    } catch (err) {
      this.logError('getFirebaseMessagingSwRegistration', err);
      return null;
    }
  }

  async fetchDeviceToken(): Promise<string | null> {

    if (this.pushService.isNativePushAvailable()) {
      const nativeToken = this.pushService.getDeviceToken();
      if (nativeToken) {
        this.deviceToken = nativeToken;
        return nativeToken;
      }
      await this.pushService.initPush();
      return this.pushService.getDeviceToken();
    }

    if (!this.isSupported) {
      return null;
    }

    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return null;
    }

    const swRegistration = await this.getFirebaseMessagingSwRegistration();

    try {
      const token = await runInInjectionContext(this.injector, () =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
          ...(swRegistration ? { serviceWorkerRegistration: swRegistration } : {})
        })
      );

      if (token) {
        this.deviceToken = token;
        return token;
      } else {
        console.warn('No token generated');
        return null;
      }
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.unsubscribeFromNotifications();
  }
}
